'use client';

import { useNavigate } from 'react-router-dom';
import ProductQuantityIncrement from '../ProductQuantityIncrement';
import { useEffect, useRef, useState } from 'react';
import LoadingButton from '../LoadingButton';
import CustomModal from '../CustomModal';
import { Tooltip } from '../Tooltip';
import { type ProductCardsProps, OutOfStock } from '../../interface/interface';
import OutOfStockOverlay from '../OutOfStockOverlay';
import { AllowedProducts } from '../AllowedProducts';
import { useGuestCart } from '../../context/guestCartContext';

const GuestProductCards = (props: ProductCardsProps) => {
  const {
    product,
    addProductToCart,
    removeProductFromCart,
    substitueCard = false,
    allowedProduct = false,
  } = props;
  const { getProductQuantity, refreshCart } = useGuestCart();

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [openSubstituteModal, setSubstituteModal] = useState<boolean>(false);
  const [suggestAccessoriesModal, setSuggestAccessories] =
    useState<boolean>(false);
  const [addProductModalLoading, setAddProductModalLoading] =
    useState<boolean>(false);

  const [showTooltip, setShowTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const productNameRef = useRef<HTMLParagraphElement>(null);

  // Get current quantity from cart context
  const orderedQuantity = getProductQuantity(product.product_code);

  const handleAddToCart = async (
    productCode: string,
    substituteForId?: string,
  ) => {
    setLoading(true);
    await addProductToCart(productCode, substituteForId);
    await refreshCart(); // Refresh cart context after adding
    setLoading(false);
  };

  const handleRemoveFromCart = async (productCode: string) => {
    setLoading(true);
    await removeProductFromCart(productCode);
    await refreshCart(); // Refresh cart context after removing
    setLoading(false);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (productNameRef.current) {
        const isOverflowing =
          productNameRef.current.scrollWidth >
            productNameRef.current.clientWidth ||
          productNameRef.current.scrollHeight >
            productNameRef.current.clientHeight;
        setIsOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const hasSubstituteAvailable =
    !substitueCard &&
    product.substituteProduct &&
    !product.substitutions_not_available;

  return (
    <>
      <li
        className="group relative flex flex-col rounded-xl border md:border-secondary-cornflowerblue-500
          bg-white text-left cursor-pointer h-full overflow-hidden 
          transition-all duration-200 ease-out
           origin-center"
        key={product.id}
      >
        <div className="flex flex-1 flex-col px-4 pt-5 pb-0">
          {/* Product image */}
          <button
            type="button"
            className="w-full flex items-center justify-center relative"
            onClick={() =>
              navigate(`/guest/product/${product.product_code}`, {
                state: {
                  from: window.location.pathname,
                },
              })
            }
          >
            <img
              className="w-full h-40 object-contain"
              src={product.image_url || '/placeholder.svg'}
              alt="product-image"
              style={{ objectFit: 'contain' }}
              id={`product-image-${product.product_code}`}
            />
            <OutOfStockOverlay
              outOfStock={product.outOfStock}
              inventoryQuantity={product.inventory_quantity}
              hasSubstitute={Boolean(product?.substituteProduct)}
              isGuest={true}
            />
          </button>

          {/* Brand + product name */}
          <div className="flex flex-col text-left gap-1 mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-cornflowerblue-600">
              {product.product_brand}
            </p>
            <div className="flex justify-between">
              <p
                className="text-xs md:text-sm font-regular text-primary-blue-500 md:two-line-ellipsis relative leading-normal line-clamp-2 min-h-9"
                ref={productNameRef}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {product.name}
              </p>
              <p className="text-xs text-grey-500 font-regular block md:hidden ml-2">
                {product.weight}
                {product.unit}
              </p>
            </div>
            {isOverflowing && showTooltip && (
              <Tooltip
                text={product.name}
                className="z-50 product-name-tooltip"
              />
            )}
          </div>

          <div className="flex md:justify-between justify-end items-center mt-6 mb-3">
            <p className="text-xs text-grey-500 font-regular hidden md:block">
              {product.weight}
              {product.unit}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-lg sm:text-md font-semibold text-primary-blue-500">
                ${product?.price}
              </span>
            </div>
          </div>

          {allowedProduct && product.allowedProducts && (
            <div className="mb-3">
              <AllowedProducts
                product={product}
                addProductToCart={handleAddToCart}
                removeProductFromCart={handleRemoveFromCart}
              />
            </div>
          )}
        </div>

        {/* Quantity stepper - full width, replaces the Add to Cart bar once in cart */}
        {orderedQuantity > 0 && (
          <div className="w-full">
            <ProductQuantityIncrement
              productCode={product.product_code}
              orderedQuantity={orderedQuantity}
              productOutOfStock={product.outOfStock}
              maxQuantity={product.productMaxQty}
              addProductToCart={handleAddToCart}
              removeProductFromCart={handleRemoveFromCart}
              minimalist={false}
              isProductTile={true}
            />
          </div>
        )}

        {/* Always-visible full-width Add to Cart / Order substitution bar */}
        {orderedQuantity === 0 &&
          (hasSubstituteAvailable ? (
            <button
              type="button"
              className="w-full bg-secondary-cornflowerblue-600 text-white text-xs md:text-sm !font-semibold !py-4
                hover:bg-white hover:text-secondary-cornflowerblue-600 transition-colors
                disabled:bg-slate-400 disabled:cursor-not-allowed hover:border-t hover:border-secondary-cornflowerblue-600"
              onClick={(e) => {
                e.stopPropagation();
                setSubstituteModal(true);
              }}
            >
              Order substitution
            </button>
          ) : (
            <LoadingButton
              isProductTile
              buttonText="Add to Cart"
              isLoading={loading}
              className="!rounded-none !py-4 text-xs md:text-sm"
              isDisabled={product.outOfStock !== OutOfStock.false || loading}
              onClick={() => {
                handleAddToCart(product.product_code);
              }}
            />
          ))}
      </li>

      {/* Modals rendered safely outside the card root container */}
      <CustomModal
        isOpen={openSubstituteModal}
        onClose={() => setSubstituteModal(false)}
        header={''}
        footer={''}
        modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
      >
        <div>
          <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
            Substitute product for:{' '}
            <span className="font-semibold">{product.name}</span>
          </p>
          <GuestProductCards
            product={product.substituteProduct!}
            addProductToCart={(id: string) =>
              handleAddToCart(id, product.product_code)
            }
            removeProductFromCart={handleRemoveFromCart}
            substitueCard={true}
            isAccessories={false}
            allowedProduct={false}
            isGuest={true}
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={suggestAccessoriesModal}
        onClose={async () => {
          setLoading(true);
          setSuggestAccessories(false);
          await handleAddToCart(product.product_code);
          setLoading(false);
        }}
        header={'Dried Herb Product Added!'}
        footer={
          <>
            <LoadingButton
              isLoading={addProductModalLoading}
              className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
              onClick={async () => {
                setAddProductModalLoading(true);
                await handleAddToCart(product.product_code);
                setAddProductModalLoading(false);
                navigate('/guest/nonscript');
              }}
              buttonText="Proceed"
            />
            <button
              className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
              onClick={async () => {
                setSuggestAccessories(false);
                setLoading(true);
                await handleAddToCart(product.product_code);
                setLoading(false);
              }}
            >
              Stay on this page
            </button>
          </>
        }
        modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
      >
        <div className="text-start">
          Would you like to purchase required accessories along with this ?
        </div>
      </CustomModal>
    </>
  );
};

export default GuestProductCards;
